/**
 * Login Page Component - Premium Minimal Design
 *
 * Clean, professional login with DataWorks branding.
 * Mobile-first responsive design.
 */

import React, { useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../services/firebase';
import { Link } from 'react-router-dom';

// Icons as inline SVG components
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

// Phoenix Logo
const PhoenixLogo = () => (
  <svg viewBox="0 0 48 48" fill="none" className="w-12 h-12 sm:w-14 sm:h-14">
    <defs>
      <linearGradient id="phoenixGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3B82F6"/>
        <stop offset="100%" stopColor="#1E40AF"/>
      </linearGradient>
    </defs>
    <circle cx="24" cy="24" r="24" fill="url(#phoenixGrad)"/>
    <path
      d="M24 12L14 20L24 28L34 20L24 12Z"
      fill="white"
      opacity="0.9"
    />
    <path
      d="M14 24L24 32L34 24"
      stroke="white"
      strokeWidth="2"
      fill="none"
      opacity="0.7"
    />
    <path
      d="M14 28L24 36L34 28"
      stroke="white"
      strokeWidth="2"
      fill="none"
      opacity="0.5"
    />
  </svg>
);

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const validateForm = (): boolean => {
    let isValid = true;
    setEmailError(null);
    setPasswordError(null);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setEmailError('Zadejte platnou emailovou adresu');
      isValid = false;
    }

    if (!password) {
      setPasswordError('Heslo je povinné');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Navigation handled by App.tsx via auth state change
      // Email verification disabled - users pay for credits so they have incentive to use real emails
    } catch (err: any) {
      console.error('Auth error:', err);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Nesprávný email nebo heslo');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Příliš mnoho pokusů. Zkuste to později.');
      } else {
        setError(err.message || 'Chyba při přihlášení');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
      }}
    >
      {/* Main Content - Centered */}
      <main className="flex-1 flex items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          {/* Logo & Brand */}
          <div className="text-center mb-8 sm:mb-10">
            <div className="flex justify-center mb-4">
              <PhoenixLogo />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-2">
              Phoenix Analytics
            </h1>
            <p className="text-slate-400 text-sm sm:text-base">
              Volební analytická platforma
            </p>
          </div>

          {/* Login Card */}
          <div
            className="rounded-2xl p-6 sm:p-8 backdrop-blur-xl"
            style={{
              background: 'rgba(30, 41, 59, 0.8)',
              border: '1px solid rgba(148, 163, 184, 0.1)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}
          >
            {/* Header */}
            <div className="mb-6 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-semibold text-white mb-1">
                Přihlášení
              </h2>
              <p className="text-slate-400 text-sm">
                Nemáte účet?{' '}
                <Link
                  to="/register"
                  className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
                >
                  Zaregistrujte se
                </Link>
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {/* Email Input */}
              <div className="mb-5">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-slate-300 mb-2"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError(null);
                  }}
                  className="w-full px-4 py-3 rounded-xl text-white placeholder-slate-500 transition-all text-base"
                  style={{
                    background: 'rgba(15, 23, 42, 0.6)',
                    border: emailError
                      ? '2px solid #ef4444'
                      : '2px solid rgba(148, 163, 184, 0.2)',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    if (!emailError) {
                      e.target.style.borderColor = '#3b82f6';
                    }
                  }}
                  onBlur={(e) => {
                    if (!emailError) {
                      e.target.style.borderColor = 'rgba(148, 163, 184, 0.2)';
                    }
                  }}
                  placeholder="vas@email.cz"
                  autoComplete="email"
                />
                {emailError && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-red-400">
                    <AlertIcon />
                    {emailError}
                  </div>
                )}
              </div>

              {/* Password Input */}
              <div className="mb-5">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-slate-300 mb-2"
                >
                  Heslo
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError(null);
                    }}
                    className="w-full px-4 py-3 pr-12 rounded-xl text-white placeholder-slate-500 transition-all text-base"
                    style={{
                      background: 'rgba(15, 23, 42, 0.6)',
                      border: passwordError
                        ? '2px solid #ef4444'
                        : '2px solid rgba(148, 163, 184, 0.2)',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      if (!passwordError) {
                        e.target.style.borderColor = '#3b82f6';
                      }
                    }}
                    onBlur={(e) => {
                      if (!passwordError) {
                        e.target.style.borderColor = 'rgba(148, 163, 184, 0.2)';
                      }
                    }}
                    placeholder="Vaše heslo"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {passwordError && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-red-400">
                    <AlertIcon />
                    {passwordError}
                  </div>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-5 h-5 rounded cursor-pointer accent-blue-500"
                    style={{
                      background: rememberMe ? '#3b82f6' : 'transparent',
                    }}
                  />
                  <span className="text-sm text-slate-400">
                    Zapamatovat si mě
                  </span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
                >
                  Zapomenuté heslo?
                </Link>
              </div>

              {/* Error Message */}
              {error && (
                <div
                  className="mb-6 px-4 py-3 rounded-xl text-sm flex items-center gap-2 bg-red-500/10 text-red-400 border border-red-500/20"
                >
                  <AlertIcon />
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 px-6 rounded-xl text-white font-semibold text-base flex items-center justify-center gap-2 transition-all"
                style={{
                  background: loading
                    ? 'rgba(59, 130, 246, 0.5)'
                    : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  boxShadow: loading
                    ? 'none'
                    : '0 4px 14px rgba(59, 130, 246, 0.4)'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(59, 130, 246, 0.4)';
                }}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <span>Přihlásit se</span>
                    <ArrowRightIcon />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 px-4 text-center">
        <div className="text-slate-500 text-xs sm:text-sm space-y-1">
          <p className="font-medium text-slate-400">DataWorks s.r.o.</p>
          <p>Vytvořil Milan Pecka</p>
          <p className="mt-2">
            <a
              href="mailto:pecka@dwx.cz"
              className="text-blue-400 hover:text-blue-300 transition-colors"
            >
              📧 pecka@dwx.cz
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
